'use client';

import React from 'react';
import Image from 'next/image';
import AppLogo from '@/components/ui/AppLogo';

const floatingCards = [
  {
    id: 'match',
    label: '94% Match Found',
    sub: 'Senior Product Designer · Figma',
    icon: '✦',
    position: 'top-[10%] left-[2%]',
    delay: '0s',
  },
  {
    id: 'interview',
    label: 'Interview Scheduled',
    sub: 'Tomorrow · 10:00 AM',
    icon: '◆',
    position: 'top-[30%] right-[1%]',
    delay: '0.5s',
  },
  {
    id: 'resume',
    label: 'Resume Analyzed',
    sub: '12 skills matched',
    icon: '▲',
    position: 'bottom-[36%] left-[1%]',
    delay: '1s',
  },
  {
    id: 'jobs',
    label: '3 Better-Fit Jobs',
    sub: 'Based on your resume',
    icon: '●',
    position: 'bottom-[18%] right-[2%]',
    delay: '1.5s',
  },
];

const valueBullets = [
  { text: 'AI-powered job matching' },
  { text: 'Resume analysis & insights' },
  { text: 'Application tracking' },
  { text: 'AI-assisted drafts' },
];

export default function AuthHeroPanel() {
  return (
    <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] relative flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-purple-50/40 to-violet-50/60">
      {/* Subtle background shapes */}
      <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-violet-100/60 blur-[80px] translate-x-1/3 -translate-y-1/4 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[320px] h-[320px] rounded-full bg-purple-100/50 blur-[60px] -translate-x-1/4 translate-y-1/4 pointer-events-none" />

      {/* Logo */}
      <div className="relative z-10 flex items-center gap-2.5 p-8 pb-0">
        <AppLogo size={30} />
        <span className="text-base font-semibold text-slate-800 tracking-tight">JobCompanion</span>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex-1 flex flex-col justify-center px-8 py-6">

        {/* Badge */}
        <div className="mb-5">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-100 border border-violet-200/80 text-[11px] font-medium text-violet-600 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
            AI-Assisted · Beta
          </div>

          {/* Headline */}
          <h1 className="text-3xl xl:text-[2rem] font-bold text-slate-800 leading-[1.25] tracking-tight">
            Find better-fit opportunities
            <span className="block mt-1 text-violet-600">
              with AI assistance.
            </span>
          </h1>
          <p className="mt-3 text-sm text-slate-500 leading-relaxed max-w-[340px]">
            You stay in control. AI helps you discover, prepare, and track — you decide what to apply to.
          </p>
        </div>

        {/* Value bullets */}
        <ul className="space-y-2 mb-8">
          {valueBullets?.map((b) => (
            <li key={b?.text} className="flex items-center gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0" />
              <span className="text-sm text-slate-600">{b?.text}</span>
            </li>
          ))}
        </ul>

        {/* Hero image + floating cards */}
        <div className="relative w-full" style={{ height: '340px' }}>
          {/* Image container */}
          <div className="absolute inset-x-6 bottom-0 top-0 rounded-2xl overflow-hidden shadow-lg shadow-slate-200/60">
            <Image
              src="/assets/images/auth_hero_light_professional.png"
              alt="Professional working on laptop in a bright modern workspace"
              fill
              className="object-cover object-top"
              sizes="(max-width: 1280px) 400px, 480px"
              priority
            />
            {/* Soft bottom fade */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-50/80 via-transparent to-transparent" />
          </div>

          {/* Floating UI cards — minimal, soft */}
          {floatingCards?.map((card) => (
            <div
              key={card?.id}
              className={`absolute ${card?.position} z-20 auth-float-card`}
              style={{ animationDelay: card?.delay }}
            >
              <div
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white/90 border border-slate-200/80 shadow-sm shadow-slate-100/60 backdrop-blur-sm"
                style={{ minWidth: '140px', maxWidth: '180px' }}
              >
                <span className="text-[10px] text-violet-500 shrink-0">{card?.icon}</span>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold text-slate-700 leading-tight truncate">{card?.label}</p>
                  <p className="text-[9px] text-slate-400 leading-tight truncate mt-0.5">{card?.sub}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom trust strip */}
      <div className="relative z-10 px-8 pb-6 pt-2">
        <div className="flex items-center gap-4 text-[10px] text-slate-400">
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            Manual Apply Active
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
            AI Assist Active
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
            Auto-Apply Coming Soon
          </span>
        </div>
      </div>
    </div>
  );
}
